import { useAtom } from "jotai";
import { useI18N } from "common/hooks";
import { drawerMenuOpenAtom } from "@/store/atoms";
import {
  publicFaqUrl,
  publicAboutUrl,
  publicPrivacyUrl,
  publicTermsUrl,
  publicTaggingInformationUrl,
  publicContactUsUrl,
  showDevTools,
} from "common/config";
import { useGetInTouchModal } from "@/hooks/useGetInTouchModal";
import { createPageAnalytics } from "common/analytics";
import { useReplayOnboardingIntroTour } from "@/features/onboarding";
import { MedalIcon, UsersIcon } from "@phosphor-icons/react";

function GetNavigationPages(
  isLoggedIn,
  setIsModalOpen,
  setIsOnboardingModalOpen,
  setOnboardingCurrentScreen,
  // Opens the "Reset onboarding" confirmation. Passed in rather than wired
  // here because the confirmation is a modal the menu owns - this file only
  // describes rows.
  onResetOnboarding,
) {
  const i18n = useI18N();
  const { showGetInTouchModal } = useGetInTouchModal();

  const handleOpenFeedbackModal = () => {
    console.log("in handleOpenFeedbackModal");
    showGetInTouchModal();
  };

  // The rebuilt six-page tour from the onboarding intro — the same thing
  // native's menu row opens. This used to open the legacy CMS carousel.
  const replayIntroTour = useReplayOnboardingIntroTour();
  const handleOpenOnboardingSliderModal = () => {
    replayIntroTour();
  };

  const [, setDrawerMenuOpen] = useAtom(drawerMenuOpenAtom);

  const toggleDrawerMenu = () => {
    setDrawerMenuOpen((drawerMenuOpen) => !drawerMenuOpen);
  };

  return [
    {
      group: "",
      groupToShowForType: "loggedin",
      // Native's first group, row for row and in its words (design QA:
      // "desktop shares the same copy and action as RN"): Badges, Activity,
      // Team. Accessibility profile stays - desktop has no set-up card to reach
      // it from the profile header the way native does.
      elements: [
        {
          name: `${i18n.get("badges")}`,
          path: "/your-badges",
          Icon: MedalIcon,
          linkToShowForType: "loggedin",
          publicUrl: "",
          onClick: () => {
            toggleDrawerMenu();
          },
        },
        {
          name: `${i18n.get("activity")}`,
          path: "/your-activity",
          icon: "/images/menu/user_activity.svg",
          iconDark: "/images/menu/user_activity_dark.svg",
          linkToShowForType: "loggedin",
          publicUrl: "",
          onClick: () => {
            // activity_viewed is fired on the activity page's mount (page_view_page) as the
            // canonical single source for every entry path — not here — to avoid double-firing
            // on menu navigation. See features/profile/UserActivity.tsx.
            toggleDrawerMenu();
          },
        },
        {
          name: `${i18n.get("yourAccessibilityProfile")}`,
          path: "/your-accessibility-profile",
          icon: "/images/menu/app_acessibility.svg",
          iconDark: "/images/menu/app_acessibility_dark.svg",
          linkToShowForType: "loggedin",
          publicUrl: "",
          onClick: () => {
            toggleDrawerMenu();
          },
        },
        {
          name: `${i18n.get("team")}`,
          path: "/your-teams",
          Icon: UsersIcon,
          linkToShowForType: "loggedin",
          publicUrl: "",
          onClick: () => {
            toggleDrawerMenu();
          },
        },
      ],
    },
    {
      group: `${i18n.get("about")}`,
      groupToShowForType: "both",
      elements: [
        {
          name: `${i18n.get("aboutSociability")}`,
          path: "/content/about",
          icon: "/images/menu/about_sociability.svg",
          iconDark: "/images/menu/about_sociability_dark.svg",
          linkToShowForType: "both",
          publicUrl: publicAboutUrl,
          onClick: () => {
            createPageAnalytics("profile_menu").track(
              "about_sociability_link_button",
              { link_destination: publicAboutUrl },
            );
            toggleDrawerMenu();
          },
        },
      ],
    },
    {
      group: `${i18n.get("helpSupport")}`,
      groupToShowForType: "both",
      elements: [
        {
          name: `${i18n.get("howTheAppWorks")}`,
          path: "",
          icon: "/images/menu/how_the_app_work.svg",
          iconDark: "/images/menu/how_the_app_work_dark.svg",
          linkToShowForType: "both",
          publicUrl: "",
          onClick: () => {
            handleOpenOnboardingSliderModal();
            toggleDrawerMenu();
          },
        },

        {
          name: `${i18n.get("taggingInformation")}`,
          path: "/content/tagging-information",
          icon: "/images/menu/tagging_info.svg",
          iconDark: "/images/menu/tagging_info_dark.svg",
          linkToShowForType: "both",
          publicUrl: publicTaggingInformationUrl,
          onClick: () => {
            createPageAnalytics("profile_menu").track(
              "tagging_information_link_button",
              { link_destination: publicTaggingInformationUrl },
            );
            toggleDrawerMenu();
          },
        },
        {
          name: `${i18n.get("faqs")}`,
          path: "/content/faq",
          icon: "/images/menu/faqs.svg",
          iconDark: "/images/menu/faqs_dark.svg",
          linkToShowForType: "both",
          publicUrl: publicFaqUrl,
          onClick: () => {
            createPageAnalytics("profile_menu").track("faqs_link_button", {
              link_destination: publicFaqUrl,
            });
            toggleDrawerMenu();
          },
        },
        {
          name: `${i18n.get("appFeedback")}`,
          path: "",
          icon: "/images/menu/app_feedback.svg",
          iconDark: "/images/menu/app_feedback_dark.svg",
          linkToShowForType: "both",
          publicUrl: publicContactUsUrl,        
          onClick: () => {
            toggleDrawerMenu();
          },
        },
      ],
    },
    {
      group: `${i18n.get("legal")}`,
      groupToShowForType: "both",
      elements: [
        {
          name: `${i18n.get("privacyPolicy")}`,
          path: "/content/privacy",
          icon: "/images/menu/privacy_policy.svg",
          iconDark: "/images/menu/privacy_policy_dark.svg",
          linkToShowForType: "both",
          publicUrl: publicPrivacyUrl,
          onClick: () => {
            toggleDrawerMenu();
          },
        },
        {
          name: `${i18n.get("termsOfUse")}`,
          path: "/content/terms-conditions",
          icon: "/images/menu/terms_of_use.svg",
          iconDark: "/images/menu/terms_of_use_dark.svg",
          linkToShowForType: "both",
          publicUrl: publicTermsUrl,
          onClick: () => {
            toggleDrawerMenu();
          },
        },
      ],
    },
    /*
     * Developer tools - non-production environments only, @see `showDevTools`.
     *
     * Spread rather than filtered further down so the group leaves no trace at
     * all in production: no empty section, no heading, nothing to reason about.
     *
     * Deliberately untracked. It is a developer's tool, and its events would be
     * noise in the menu funnel - the same call native makes for its row.
     */
    ...(showDevTools
      ? [
          {
            group: `${i18n.get("developerTools")}`,
            groupToShowForType: "both",
            elements: [
              {
                name: `${i18n.get("resetOnboarding")}`,
                path: "",
                // No icon: there is no reset glyph in the menu set, and a
                // developer row is not worth inventing an asset for. The
                // renderer leaves an aligned gap - @see `HamburgerMenu`.
                icon: "",
                iconDark: "",
                linkToShowForType: "both",
                publicUrl: "",
                onClick: () => {
                  // The drawer deliberately stays open: the confirmation is the
                  // next step, and closing first would pull the rug from under
                  // a user who then cancels. `handleResetOnboarding` reloads the
                  // page once they commit.
                  onResetOnboarding();
                },
              },
            ],
          },
        ]
      : []),
  ];
}

export default GetNavigationPages;
